import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Globe, Building, ChevronRight, FlaskConical, Award } from 'lucide-react';
import PageTemplate from '../../components/Common/PageTemplate';

const ProjectCard = ({ title, description, icon: Icon, onClick, gradient, iconBg, viewText }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      whileHover={{ y: -8, scale: 1.02 }}
      transition={{ duration: 0.3 }}
      onClick={onClick}
      className="group relative cursor-pointer"
    >
      <div className="relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-slate-100 overflow-hidden h-full">
        {/* Gradient Background */}
        <div className={`absolute inset-0 bg-gradient-to-br ${gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

        {/* Icon */}
        <div className="relative z-10 mb-6">
          <div className={`inline-flex p-4 rounded-2xl ${iconBg} group-hover:scale-110 transition-transform duration-500`}>
            <Icon className="w-8 h-8 text-white" strokeWidth={2} />
          </div>
        </div>

        {/* Content */}
        <div className="relative z-10">
          <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-blue-600 transition-colors">
            {title}
          </h3>
          <p className="text-slate-600 leading-relaxed mb-6">
            {description}
          </p>

          {/* Arrow */}
          <div className="inline-flex items-center gap-2 text-blue-600 font-semibold group-hover:gap-4 transition-all">
            <span>{viewText}</span>
            <ChevronRight className="w-5 h-5 group-hover:translate-x-2 transition-transform" />
          </div>
        </div>

        {/* Decorative Element */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-blue-500/10 to-purple-500/10 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
      </div>
    </motion.div>
  );
};

const DevamEdenProjeler = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const prefix = i18n.language === 'en' ? '/en' : '';

  const handleExternalProjects = () => {
    navigate(prefix + '/arastirma/devam-eden-projelerimiz/dis-destekli');
  };

  const handleBAPProjects = () => {
    navigate(prefix + '/arastirma/devam-eden-projelerimiz/bap-destekli');
  };

  return (
    <PageTemplate
      title={t('pages.ongoingProjects.title')}
      subtitle={t('pages.ongoingProjects.subtitle')}
    >
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          {/* Hero Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-16"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 text-blue-600 rounded-full text-sm font-semibold mb-6">
              <FlaskConical className="w-4 h-4" />
              {t('pages.ongoingProjects.selectCategory')}
            </div>
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
              {t('pages.ongoingProjects.chooseProjectType')}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t('pages.ongoingProjects.selectDescription')}
            </p>
          </motion.div>

          {/* Project Type Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">

            {/* External Projects Card */}
            <ProjectCard
              title={t('pages.ongoingProjects.externalProjects')}
              description={t('pages.ongoingProjects.externalProjectsDesc')}
              icon={Globe}
              onClick={handleExternalProjects}
              gradient="from-blue-500 to-cyan-500"
              iconBg="bg-gradient-to-br from-blue-500 to-cyan-500"
              viewText={t('pages.ongoingProjects.viewProjects')}
            />

            {/* BAP Projects Card */}
            <ProjectCard
              title={t('pages.ongoingProjects.bapProjects')}
              description={t('pages.ongoingProjects.bapProjectsDesc')}
              icon={Building}
              onClick={handleBAPProjects}
              gradient="from-violet-500 to-purple-500"
              iconBg="bg-gradient-to-br from-violet-500 to-purple-500"
              viewText={t('pages.ongoingProjects.viewProjects')}
            />

          </div>

          {/* Stats Section - Enhanced Design */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
            className="mt-24 max-w-6xl mx-auto"
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">

              {/* Active Research Card */}
              <motion.div
                whileHover={{ y: -8, scale: 1.03 }}
                transition={{ duration: 0.3 }}
                className="group relative overflow-hidden"
              >
                <div className="relative bg-gradient-to-br from-blue-50 via-white to-cyan-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-blue-100">
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-cyan-500 opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>

                  {/* Decorative Circle */}
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-blue-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>

                  {/* Icon Container */}
                  <div className="relative z-10 mb-6 flex justify-center">
                    <div className="relative">
                      <div className="absolute inset-0 bg-blue-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
                      <div className="relative bg-gradient-to-br from-blue-500 to-cyan-500 p-5 rounded-2xl shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                        <Award className="w-10 h-10 text-white" strokeWidth={2.5} />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-3 group-hover:scale-105 transition-transform">
                      {t('pages.ongoingProjects.activeResearch')}
                    </h3>
                    <p className="text-slate-600 font-medium text-sm leading-relaxed">
                      {t('pages.ongoingProjects.ongoingProjects')}
                    </p>
                  </div>

                  {/* Bottom Line Accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 to-cyan-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </div>
              </motion.div>

              {/* International Card */}
              <motion.div
                whileHover={{ y: -8, scale: 1.03 }}
                transition={{ duration: 0.3 }}
                className="group relative overflow-hidden"
              >
                <div className="relative bg-gradient-to-br from-violet-50 via-white to-purple-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-violet-100">
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-violet-500 to-purple-500 opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>

                  {/* Decorative Circle */}
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-violet-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>

                  {/* Icon Container */}
                  <div className="relative z-10 mb-6 flex justify-center">
                    <div className="relative">
                      <div className="absolute inset-0 bg-violet-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
                      <div className="relative bg-gradient-to-br from-violet-500 to-purple-500 p-5 rounded-2xl shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                        <Globe className="w-10 h-10 text-white" strokeWidth={2.5} />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent mb-3 group-hover:scale-105 transition-transform">
                      {t('pages.ongoingProjects.international')}
                    </h3>
                    <p className="text-slate-600 font-medium text-sm leading-relaxed">
                      {t('pages.ongoingProjects.collaboration')}
                    </p>
                  </div>

                  {/* Bottom Line Accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 to-purple-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </div>
              </motion.div>

              {/* Institutional Card */}
              <motion.div
                whileHover={{ y: -8, scale: 1.03 }}
                transition={{ duration: 0.3 }}
                className="group relative overflow-hidden"
              >
                <div className="relative bg-gradient-to-br from-emerald-50 via-white to-teal-50 rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-emerald-100">
                  {/* Animated Background */}
                  <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 to-teal-500 opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>

                  {/* Decorative Circle */}
                  <div className="absolute -top-10 -right-10 w-32 h-32 bg-emerald-500/10 rounded-full blur-2xl group-hover:scale-150 transition-transform duration-700"></div>

                  {/* Icon Container */}
                  <div className="relative z-10 mb-6 flex justify-center">
                    <div className="relative">
                      <div className="absolute inset-0 bg-emerald-500 rounded-2xl blur-xl opacity-50 group-hover:opacity-70 transition-opacity"></div>
                      <div className="relative bg-gradient-to-br from-emerald-500 to-teal-500 p-5 rounded-2xl shadow-lg group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                        <Building className="w-10 h-10 text-white" strokeWidth={2.5} />
                      </div>
                    </div>
                  </div>

                  {/* Content */}
                  <div className="relative z-10 text-center">
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-3 group-hover:scale-105 transition-transform">
                      {t('pages.ongoingProjects.institutional')}
                    </h3>
                    <p className="text-slate-600 font-medium text-sm leading-relaxed">
                      {t('pages.ongoingProjects.support')}
                    </p>
                  </div>

                  {/* Bottom Line Accent */}
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500"></div>
                </div>
              </motion.div>

            </div>
          </motion.div>

        </div>
      </div>
    </PageTemplate>
  );
};

export default DevamEdenProjeler;
